import { useState, useEffect } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem,
  LinearProgress,
  AvatarGroup,
  Avatar,
  Stack,
  Divider,
  Chip,
  Box,
  Alert
} from '@mui/material';
import { 
  PieChart, 
  BarChart,
  LineChart
} from '@mui/x-charts';
import {
  FilterList,
  Refresh,
  ErrorOutline,
  WarningAmber,
  CheckCircleOutline,
  Timeline,
  Group,
  Assignment
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';

const DashboardPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [timeFilter, setTimeFilter] = useState('month');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    overview: {
      totalProjects: 0,
      activeProjects: 0,
      completedProjects: 0,
      completionRate: 0,
      overdueProjects: 0,
      highPriorityIssues: 0
    },
    statusDistribution: {
      labels: [],
      datasets: [{ data: [], backgroundColor: [] }]
    },
    teamPerformance: [],
    projectTimelines: [],
    recentActivities: []
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await API.get('/projects/projects-analytics');
        setDashboardData(res.data.data);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [timeFilter]);

  const handleFilterOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleTimeFilterChange = (filter) => {
    setTimeFilter(filter);
    handleFilterClose();
  };

  const handleRefresh = () => {
    // Implement refresh logic
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>Loading dashboard data...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }}>
      {/* Header */}
      <Grid item xs={12}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Projects Analytics Dashboard
          </Typography>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handleFilterOpen}>
              <FilterList />
            </IconButton>
            <IconButton onClick={handleRefresh}>
              <Refresh />
            </IconButton>
          </Stack>
        </Stack>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleFilterClose}
        >
          {['week', 'month', 'quarter', 'year'].map((filter) => (
            <MenuItem 
              key={filter}
              onClick={() => handleTimeFilterChange(filter)}
              selected={timeFilter === filter}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </MenuItem>
          ))}
        </Menu>
      </Grid>

      {/* Quick Stats Row */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Assignment fontSize="large" color="primary" />
            <div>
              <Typography variant="h6">Total Projects</Typography>
              <Typography variant="h3">{dashboardData.overview.totalProjects}</Typography>
            </div>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1}>
            <Chip 
              label={`${dashboardData.overview.activeProjects} Active`} 
              color="primary" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              label={`${dashboardData.overview.completedProjects} Completed`} 
              color="success" 
              variant="outlined" 
              size="small" 
            />
          </Stack>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <CheckCircleOutline fontSize="large" color="success" />
            <div>
              <Typography variant="h6">Completion Rate</Typography>
              <Typography variant="h3">{dashboardData.overview.completionRate}%</Typography>
            </div>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress 
              variant="determinate" 
              value={dashboardData.overview.completionRate} 
              color="success"
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Group fontSize="large" color="secondary" />
            <div>
              <Typography variant="h6">Teams Performance</Typography>
              <Typography variant="h3">
                {dashboardData.teamPerformance.length} Teams
              </Typography>
            </div>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <AvatarGroup max={4} sx={{ justifyContent: 'center' }}>
            {dashboardData.teamPerformance.slice(0, 5).map((team, index) => (
              <Avatar key={index} sx={{ bgcolor: theme.palette.secondary.main }}>
                {team.teamName?.[0] || 'T'}
              </Avatar>
            ))}
          </AvatarGroup>
        </Paper>
      </Grid>

      {/* Status Distribution - Only render if we have data */}
      {dashboardData.statusDistribution?.labels?.length > 0 && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Projects by Status
            </Typography>
            <PieChart
              series={[{
                data: dashboardData.statusDistribution.labels.map((label, index) => ({
                  value: dashboardData.statusDistribution.datasets[0].data[index],
                  label,
                  color: dashboardData.statusDistribution.datasets[0].backgroundColor[index]
                })),
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
              }]}
            />
          </Paper>
        </Grid>
      )}

      {/* Team Performance - Only render if we have data */}
      {dashboardData.teamPerformance?.length > 0 && (
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Team Performance
            </Typography>
            <BarChart
              xAxis={[{
                scaleType: 'band',
                data: dashboardData.teamPerformance.map(team => team.teamName || 'Unnamed Team'),
              }]}
              series={[
                {
                  data: dashboardData.teamPerformance.map(team => team.avgCompletion || team.completionRate || 0),
                  label: 'Avg Completion %',
                  color: theme.palette.primary.main
                },
                {
                  data: dashboardData.teamPerformance.map(team => team.projectCount || 0),
                  label: 'Projects Assigned',
                  color: theme.palette.secondary.main
                }
              ]}
            />
          </Paper>
        </Grid>
      )}

      {/* Projects Timeline - Only render if we have data */}
      {dashboardData.projectTimelines?.length > 0 && (
        <Grid item xs={12}>
          <Paper sx={{ p: 2, height: '400px' }}>
            <Typography variant="h6" gutterBottom>
              Projects Timeline vs Completion
            </Typography>
            <LineChart
              xAxis={[{
                data: dashboardData.projectTimelines.map(project => project.name || 'Unnamed Project'),
                scaleType: 'point'
              }]}
              series={[
                {
                  data: dashboardData.projectTimelines.map(project => project.timeElapsed || 0),
                  label: 'Time Elapsed %',
                  color: theme.palette.warning.main
                },
                {
                  data: dashboardData.projectTimelines.map(project => project.completion || project.completionRate || 0),
                  label: 'Completion %',
                  color: theme.palette.success.main
                }
              ]}
            />
          </Paper>
        </Grid>
      )}

      {/* Recent Activities */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activities
            <Timeline sx={{ verticalAlign: 'middle', ml: 1 }} />
          </Typography>
          {dashboardData.recentActivities?.length > 0 ? (
            <Stack spacing={2}>
              {dashboardData.recentActivities.map((activity) => (
                <Paper key={activity.id} sx={{ p: 1.5, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                  <Stack direction="row" justifyContent="space-between">
                    <div>
                      <Typography variant="subtitle2">{activity.type}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {activity.project} • {activity.user}
                      </Typography>
                    </div>
                    <Typography variant="caption" color="text.secondary">
                      {activity.time}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No recent activities
            </Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default DashboardPage;